import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { setCurrentPageTitle, setCurrentRoute } from "../store/layoutUI";

const LANDING_PAGE_STYLES = {
  shell: {
    fontFamily: '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    padding: "0",
    width: "100%",
    maxWidth: "100%",
    flex: 1,
    height: "100%",
  },
  hero: {
    position: "relative",
    overflow: "hidden",
    borderRadius: "24px",
    padding: "36px 38px",
    background: "linear-gradient(135deg, #eff6ff 0%, #ffffff 52%, #f8fafc 100%)",
    boxShadow: "0 18px 40px rgba(37, 99, 235, 0.08)",
    border: "1px solid rgba(191, 219, 254, 0.9)",
    color: "#0f172a",
  },
  heroGlow: {
    position: "absolute",
    top: "-100px",
    right: "-80px",
    width: "350px",
    height: "350px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(59, 130, 246, 0.12) 0%, rgba(59, 130, 246, 0) 70%)",
    pointerEvents: "none",
  },
  heroGlowSecondary: {
    position: "absolute",
    bottom: "-80px",
    left: "-60px",
    width: "280px",
    height: "280px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(147, 51, 234, 0.06) 0%, rgba(147, 51, 234, 0) 70%)",
    pointerEvents: "none",
  },
  heroRow: {
    position: "relative",
    zIndex: 2,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "24px",
    flexWrap: "wrap",
  },
  heroCopy: {
    minWidth: "300px",
    flex: "1 1 540px",
  },
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "12px",
    fontWeight: 800,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#1d4ed8",
    background: "#ffffff",
    padding: "6px 14px",
    borderRadius: "999px",
    border: "1px solid rgba(191, 219, 254, 0.95)",
    marginBottom: "16px",
    boxShadow: "0 6px 16px rgba(37, 99, 235, 0.06)",
  },
  title: {
    fontSize: "32px",
    lineHeight: 1.18,
    fontWeight: 800,
    color: "#0f172a",
    margin: "0 0 14px 0",
    letterSpacing: "-0.02em",
  },
  subtitle: {
    fontSize: "15px",
    lineHeight: 1.6,
    color: "#475569",
    margin: 0,
    maxWidth: "680px",
  },
  statsWrap: {
    display: "flex",
    gap: "14px",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    flex: "0 0 auto",
  },
  statCard: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "14px 20px",
    borderRadius: "16px",
    background: "#ffffff",
    border: "1px solid rgba(191, 219, 254, 0.9)",
    minWidth: "125px",
    boxShadow: "0 8px 20px rgba(37, 99, 235, 0.06)",
  },
  statNumber: {
    fontSize: "22px",
    fontWeight: 800,
    color: "#1d4ed8",
    lineHeight: 1,
  },
  statLabel: {
    fontSize: "11px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "#64748b",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1.35fr 1fr",
    gap: "22px",
    alignItems: "stretch",
    flex: 1,
  },
  panel: {
    backgroundColor: "#ffffff",
    border: "1px solid rgba(226, 232, 240, 0.9)",
    borderRadius: "22px",
    padding: "26px",
    boxShadow: "0 14px 35px rgba(15, 23, 42, 0.05)",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  panelHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "16px",
  },
  sectionLabel: {
    fontSize: "11px",
    fontWeight: 800,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#2563eb",
  },
  sectionTitle: {
    fontSize: "22px",
    fontWeight: 800,
    color: "#0f172a",
    margin: "4px 0 10px 0",
    letterSpacing: "-0.01em",
  },
  sectionText: {
    fontSize: "14px",
    lineHeight: 1.65,
    color: "#64748b",
    margin: "0 0 20px 0",
  },
  quickGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "16px",
  },
  quickCard: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    textDecoration: "none",
    background: "#ffffff",
    border: "1px solid rgba(226, 232, 240, 0.95)",
    borderRadius: "18px",
    padding: "20px",
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.04)",
    minHeight: "165px",
    transition: "all 0.25s ease",
  },
  quickIcon: {
    width: "44px",
    height: "44px",
    borderRadius: "14px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
    color: "#ffffff",
    fontSize: "18px",
    marginBottom: "14px",
    boxShadow: "0 10px 22px rgba(37, 99, 235, 0.22)",
  },
  quickTitle: {
    fontSize: "16px",
    fontWeight: 800,
    color: "#0f172a",
    marginBottom: "6px",
  },
  quickText: {
    fontSize: "13px",
    lineHeight: 1.5,
    color: "#64748b",
    marginBottom: "14px",
  },
  quickLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12px",
    fontWeight: 800,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#2563eb",
  },
  checklist: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  checklistRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "14px",
    padding: "14px 16px",
    borderRadius: "16px",
    background: "#f8fafc",
    border: "1px solid rgba(226, 232, 240, 0.9)",
    transition: "all 0.2s ease",
  },
  checklistDot: {
    width: "32px",
    height: "32px",
    borderRadius: "10px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
    fontSize: "13px",
    fontWeight: 800,
    flexShrink: 0,
  },
  checklistTitle: {
    fontSize: "15px",
    fontWeight: 700,
    color: "#0f172a",
    marginBottom: "4px",
  },
  checklistText: {
    fontSize: "13px",
    lineHeight: 1.55,
    color: "#64748b",
  },
};

export default function LandingPage() {
  const usrType = localStorage.getItem("userType");
  const dispatch = useDispatch();

  const route = [{ name: "Home", url: "" }];

  useEffect(() => {
    dispatch(setCurrentPageTitle("Home"));
    dispatch(setCurrentRoute(route));
  }, [dispatch]);

  const isCandidate = usrType === "candidate";
  const portalLabel = isCandidate ? "Candidate Workspace" : "Partner Workspace";

  const quickActions = isCandidate
    ? [
        {
          title: "Continue Onboarding",
          description:
            "Pick up your onboarding tasks, complete required forms, and move your application forward.",
          href: "/Onboarding",
          icon: "fa fa-rocket",
        },
        {
          title: "Contract Documents",
          description:
            "Review and complete contract-related documents in one central hub.",
          href: "/ContractDocs",
          icon: "fa fa-file-text-o",
        },
        {
          title: "Background Checks",
          description:
            "Track pending verification checks and submit any remaining items quickly.",
          href: "/BackgroundChecks",
          icon: "fa fa-shield",
        },
        {
          title: "Reporting Details",
          description:
            "Update reporting info and keep project details fully up to date.",
          href: "/ReportingDetails",
          icon: "fa fa-address-card-o",
        },
      ]
    : [
        {
          title: "Partner Candidates",
          description:
            "Review candidate activity, progress, and onboarding readiness from one dashboard.",
          href: "/PartnerCandidates",
          icon: "fa fa-users",
        },
        {
          title: "Partner Invoices",
          description:
            "Check invoice workflows, status, and submissions without leaving the portal.",
          href: "/PartnerInvoices",
          icon: "fa fa-money",
        },
        {
          title: "Manage Users",
          description:
            "Maintain partner user access, permissions, and security visibility across the account.",
          href: "/ManagePartnerUsers",
          icon: "fa fa-user-circle-o",
        },
        {
          title: "Compliance Reports",
          description:
            "Open compliance reporting tools and monitor key partner obligations.",
          href: "/ComplianceRpt",
          icon: "fa fa-line-chart",
        },
      ];

  const checklistItems = isCandidate
    ? [
        {
          title: "Navigate via the Left Menu",
          text: "Use the side menu to move through onboarding, contracts, reporting, and required tasks.",
        },
        {
          title: "Complete Steps Sequentially",
          text: "Finishing each step in sequence ensures seamless progress without missing dependencies.",
        },
        {
          title: "Central Launchpad",
          text: "Return to this home dashboard anytime to access your most frequently used sections.",
        },
      ]
    : [
        {
          title: "Open Operational Tools",
          text: "Use the quick action cards to jump directly into candidate pools, invoices, and user access.",
        },
        {
          title: "Maintain Account Compliance",
          text: "Updated contact, compliance, and user information keeps your partner team operating smoothly.",
        },
        {
          title: "Unified Partner Dashboard",
          text: "This landing experience brings your primary partner workflows into an easy, single-click view.",
        },
      ];

  return (
    <React.Fragment>
      <style>{`
        .quick-action-card:hover {
          transform: translateY(-4px) !important;
          box-shadow: 0 16px 35px rgba(37, 99, 235, 0.12) !important;
          border-color: rgba(147, 197, 253, 0.9) !important;
        }
        .quick-action-card:hover .quick-action-link {
          color: #1d4ed8 !important;
        }
        .quick-action-card:hover .quick-action-icon {
          transform: scale(1.05);
        }
        .checklist-item-row:hover {
          background: #ffffff !important;
          box-shadow: 0 8px 20px rgba(15, 23, 42, 0.05) !important;
          border-color: rgba(191, 219, 254, 0.8) !important;
        }
        @media (max-width: 992px) {
          .landing-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      <div style={LANDING_PAGE_STYLES.shell}>
        {/* Hero Section */}
        <div style={LANDING_PAGE_STYLES.hero}>
          <div style={LANDING_PAGE_STYLES.heroGlow}></div>
          <div style={LANDING_PAGE_STYLES.heroGlowSecondary}></div>
          <div style={LANDING_PAGE_STYLES.heroRow}>
            <div style={LANDING_PAGE_STYLES.heroCopy}>
              <h1 style={LANDING_PAGE_STYLES.title}>
                Human Resources Onboarding and Contract Management System
              </h1>
              <p style={LANDING_PAGE_STYLES.subtitle}>
                Select items from the left navigation menu to review tasks, manage documents, and complete your onboarding workflow seamlessly.
              </p>
            </div>

            <div style={LANDING_PAGE_STYLES.statsWrap}>
              <div style={LANDING_PAGE_STYLES.statCard}>
                <span style={LANDING_PAGE_STYLES.statNumber}>100%</span>
                <span style={LANDING_PAGE_STYLES.statLabel}>Secure Access</span>
              </div>
              <div style={LANDING_PAGE_STYLES.statCard}>
                <span style={LANDING_PAGE_STYLES.statNumber}>Active</span>
                <span style={LANDING_PAGE_STYLES.statLabel}>Portal Status</span>
              </div>
              <div style={LANDING_PAGE_STYLES.statCard}>
                <span style={LANDING_PAGE_STYLES.statNumber}>24 / 7</span>
                <span style={LANDING_PAGE_STYLES.statLabel}>Available</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="landing-grid" style={LANDING_PAGE_STYLES.grid}>
          {/* Quick Actions Panel */}
          <div style={LANDING_PAGE_STYLES.panel}>
            <div style={LANDING_PAGE_STYLES.panelHeader}>
              <span style={LANDING_PAGE_STYLES.sectionLabel}>Quick Actions</span>
              <span className="badge badge-pill badge-primary" style={{ fontSize: "11px", padding: "6px 12px" }}>
                Fast Launch
              </span>
            </div>
            <h2 style={LANDING_PAGE_STYLES.sectionTitle}>
              Jump straight into your workspace
            </h2>
            <p style={LANDING_PAGE_STYLES.sectionText}>
              Open your key tasks and primary portal modules directly without hunting through the navigation hierarchy.
            </p>

            <div style={LANDING_PAGE_STYLES.quickGrid}>
              {quickActions.map((action) => (
                <a
                  key={action.title}
                  href={action.href}
                  className="quick-action-card"
                  style={LANDING_PAGE_STYLES.quickCard}
                >
                  <div>
                    <span className="quick-action-icon" style={LANDING_PAGE_STYLES.quickIcon}>
                      <i className={action.icon} aria-hidden="true"></i>
                    </span>
                    <div style={LANDING_PAGE_STYLES.quickTitle}>
                      {action.title}
                    </div>
                    <div style={LANDING_PAGE_STYLES.quickText}>
                      {action.description}
                    </div>
                  </div>
                  <div className="quick-action-link" style={LANDING_PAGE_STYLES.quickLink}>
                    Open Module <i className="fa fa-arrow-right ml-1" aria-hidden="true"></i>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Getting Started / Instructions Panel */}
          <div style={LANDING_PAGE_STYLES.panel}>
            <div style={LANDING_PAGE_STYLES.panelHeader}>
              <span style={LANDING_PAGE_STYLES.sectionLabel}>Getting Started</span>
              <span className="badge badge-pill badge-info" style={{ fontSize: "11px", padding: "6px 12px" }}>
                Guide
              </span>
            </div>
            <h2 style={LANDING_PAGE_STYLES.sectionTitle}>
              Streamlined Portal Navigation
            </h2>
            <p style={LANDING_PAGE_STYLES.sectionText}>
              This landing page is designed to keep your next best action clear while providing instant access to all core services.
            </p>

            <div style={LANDING_PAGE_STYLES.checklist}>
              {checklistItems.map((item, index) => (
                <div
                  key={item.title}
                  className="checklist-item-row"
                  style={LANDING_PAGE_STYLES.checklistRow}
                >
                  <div style={LANDING_PAGE_STYLES.checklistDot}>
                    {index + 1}
                  </div>
                  <div>
                    <div style={LANDING_PAGE_STYLES.checklistTitle}>
                      {item.title}
                    </div>
                    <div style={LANDING_PAGE_STYLES.checklistText}>
                      {item.text}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}
